import React, { useCallback, useContext, useEffect, useState } from "react";
import "./courses-content.component.scoped.scss";
import { Box, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import CreateCourseDialog from "../../../modals/create-course/create-course.dialog";
import { history } from "react-router-guard";
import { GlobalContext } from "../../../../../store";
import { UserRoles } from "../../../../../types/userRoles";
import { Skeleton } from "@material-ui/lab";
import { services } from "../../../../../services";
import AppButtonComponent from "../../../../../components/form/app-button";
import {
  CompletionStatus,
  CourseFilters,
  CourseStatus,
} from "../../../types/courses.types";
import CoursesFilterComponent from "../courses-filter/courses-filter.component";
import AppChipsComponent from "../../../../../components/form/app-chips";
import { educationRoutes } from "../../../types/education.routes";
import { icons } from "../../../../../statics/icons";

const CoursesContentComponent = () => {
  const { t } = useTranslation();
  const { user } = useContext(GlobalContext);
  const hasFullAccess = UserRoles.FULL_ACCESS_ROLES.includes(user.role);

  const dummyCourses = new Array(8).fill(null);
  const [loading, setLoading] = useState(true);
  const [showCreateCourseModal, setShowCreateCourseModal] = useState(false);
  const [courses, setCourses] = useState<any[]>(dummyCourses);
  const [filters, setFilters] = useState<CourseFilters>({
    categoryIds: [],
    searchText: "",
    status: null,
    completionStatus: CompletionStatus.ALL,
  });

  const handleCloseCreateCourseModal = (value: any) => {
    setTimeout(() => {
      setShowCreateCourseModal(false);
    }, 0);
    if (value) {
      fetchCourses();
    }
  };

  const fetchCourses = useCallback(async () => {
    try {
      setLoading(true);
      setCourses(dummyCourses);
      // Build query params from filters
      const params: any = {};

      if (filters.categoryIds.length > 0) {
        params.categoryIds = filters.categoryIds.join(",");
      }

      if (filters.searchText.trim()) {
        params.search = filters.searchText.trim();
      }

      if (filters.status !== null) {
        params.status = filters.status;
      }

      if (filters.completionStatus !== CompletionStatus.ALL) {
        params.progressStatus = filters.completionStatus;
      }

      const response = await services.courses.list(params);
      setCourses(response || []);
    } catch (error) {
      console.error("Error fetching courses:", error);
      setCourses([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  const handleFilterChange = useCallback((newFilters: CourseFilters) => {
    setFilters(newFilters);
  }, []);

  const navigator = (type: string) => {
    history.push(type);
  };

  return (
    <Box className="courses__content mb-5">
      <Box className="global__box__content p-0">
        <Box className="global__box__content__header">
          <Typography variant="h2" className="text-capitalize">
            {t("EDUCATION_PAGE.COURSES_LIST")}
          </Typography>
          {hasFullAccess && (
            <Box className="global__box__content__action">
              <Typography variant="body1" style={{ color: "var(--hint-text)" }}>
                {t("EDUCATION_PAGE.CREATE_COURSE")}
              </Typography>
              <AppButtonComponent
                variant="contained"
                color="primary"
                size="medium"
                onClick={() => setShowCreateCourseModal(true)}
              >
                {t("CREATE")}
              </AppButtonComponent>
            </Box>
          )}
        </Box>
        <CoursesFilterComponent onFilterChange={handleFilterChange} />

        {courses.length ? (
          <Box className="courses__list">
            {courses.map((course, index: number) => (
              <Box
                className={`course__card ${
                  !loading && course.status === CourseStatus.INACTIVE
                    ? "_inactive"
                    : ""
                }`}
                key={index}
                onClick={() =>
                  !loading &&
                  (hasFullAccess || course.status !== CourseStatus.INACTIVE) &&
                  course.id &&
                  navigator(educationRoutes.course(course.id))
                }
                style={{
                  cursor:
                    loading ||
                    (!hasFullAccess && course.status === CourseStatus.INACTIVE)
                      ? "default"
                      : "pointer",
                }}
              >
                <Box className="course__image">
                  {loading ? (
                    <Skeleton variant="rect" height="15rem" width="100%" />
                  ) : (
                    <img src={course.imageURL} alt={course.title} />
                  )}

                  {!loading &&
                  course &&
                  (course.userStatus?.status === CourseStatus.COMPLETED ||
                    course.status === CourseStatus.INACTIVE) ? (
                    <Box className="course__image__overlay">
                      <Box
                        className="course__image__overlay__icon"
                        style={{
                          background:
                            course.status === CourseStatus.INACTIVE
                              ? "#071437"
                              : "#aacd34",
                        }}
                      >
                        <img
                          src={
                            course.status === CourseStatus.INACTIVE
                              ? icons.LOCK
                              : icons.PAGES.EDUCATION.COMPLETED
                          }
                          alt={
                            course.status === CourseStatus.INACTIVE
                              ? "Lock"
                              : "Completed"
                          }
                        />
                      </Box>
                    </Box>
                  ) : null}
                </Box>
                <Box className="course__info p-3">
                  {loading ? (
                    <Skeleton
                      variant="text"
                      height={20}
                      width="80%"
                      style={{ marginBottom: "10px" }}
                    />
                  ) : (
                    <Typography variant="h3" color="textPrimary">
                      {course.title}
                    </Typography>
                  )}
                  {loading ? (
                    <>
                      <Skeleton
                        variant="text"
                        height={18}
                        width="100%"
                        style={{ marginBottom: "4px" }}
                      />
                      <Skeleton
                        variant="text"
                        height={18}
                        width="90%"
                        style={{ marginBottom: "4px" }}
                      />
                      <Skeleton
                        variant="text"
                        height={18}
                        width="70%"
                        style={{ marginBottom: "16px" }}
                      />
                    </>
                  ) : (
                    <Typography
                      variant="body1"
                      className="mb-4 mt-2"
                      color="textSecondary"
                    >
                      {course.brief}
                    </Typography>
                  )}

                  <Box className="course__categories">
                    {loading ? (
                      <Skeleton
                        variant="rect"
                        height={32}
                        width={100}
                        style={{ borderRadius: "6px" }}
                      />
                    ) : (
                      <AppChipsComponent
                        label={course.category.name}
                        color="secondary"
                        variant="default"
                        size="medium"
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Box className="no__courses__found text-center my-5">
            <Typography variant="h3" style={{ color: "#78829D" }}>
              {t("EDUCATION_PAGE.NO_COURSES_FOUND")}
            </Typography>
          </Box>
        )}
      </Box>
      {showCreateCourseModal && (
        <CreateCourseDialog
          show={showCreateCourseModal}
          onClose={(value: any) => handleCloseCreateCourseModal(value)}
        />
      )}
    </Box>
  );
};

export default CoursesContentComponent;
